import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useTheme, applyAccent, applyFontPair } from "@/lib/theme";
import { canSee } from "@/lib/sections";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  CalendarDays,
  BedDouble,
  ClipboardList,
  Contact,
  Sparkles,
  FileText,
  FileSpreadsheet,
  BarChart3,
  Users as UsersIcon,
  LogOut,
  Sun,
  Moon,
  Settings as SettingsIcon,
  Theater,
  UtensilsCrossed,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Panoramica", icon: LayoutDashboard, end: true },
  { to: "/calendario", label: "Calendario", icon: CalendarDays, section: "calendar" },
  { to: "/prenotazioni", label: "Prenotazioni", icon: ClipboardList, section: "bookings" },
  { to: "/ospiti", label: "Ospiti", icon: Contact, section: "guests" },
  { to: "/camere/nord", label: "Camere Edificio Nord", icon: BedDouble, section: "rooms" },
  { to: "/camere/sud", label: "Camere Edificio Sud", icon: BedDouble, section: "rooms" },
  { to: "/pulizie", label: "Pulizie", icon: Sparkles, section: "cleaning" },
  { to: "/adempimenti", label: "Schedine & ISTAT", icon: FileSpreadsheet, section: "exports" },
  { to: "/fatture", label: "Fatture & Ricevute", icon: FileText, section: "invoices" },
  { to: "/report/nord", label: "Report Incassi Nord", icon: BarChart3, section: "reports" },
  { to: "/report/sud", label: "Report Incassi Sud", icon: BarChart3, section: "reports" },
  { to: "/auditorium", label: "Calendario Auditorium", icon: Theater, section: "auditorium" },
  { to: "/mensa", label: "Mensa", icon: UtensilsCrossed, section: "mensa" },
  { to: "/utenti", label: "Utenti & Ruoli", icon: UsersIcon, admin: true },
  { to: "/impostazioni", label: "Impostazioni", icon: SettingsIcon, admin: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { dark, toggleTheme } = useTheme(user);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const [structureName, setStructureName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoDarkUrl, setLogoDarkUrl] = useState("");
  const shownLogo = (dark && logoDarkUrl) || logoUrl;
  useEffect(() => {
    api.get("/settings").then((r) => {
      setStructureName(r.data.structure_name);
      setLogoUrl(r.data.logo_url || "");
      setLogoDarkUrl(r.data.logo_dark_url || "");
      applyAccent(r.data.accent_color);
      applyFontPair(r.data.font_pair);
      if (r.data.logo_url) {
        ["icon", "apple-touch-icon"].forEach((rel) => {
          let link = document.querySelector(`link[rel="${rel}"]`);
          if (!link) { link = document.createElement("link"); link.rel = rel; document.head.appendChild(link); }
          link.href = r.data.logo_url;
        });
      }
    }).catch(() => {});
  }, []);
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-card hidden md:flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-border flex items-center gap-3">
          {shownLogo && <img src={shownLogo} alt="" data-testid="sidebar-logo" className="w-11 h-11 object-contain rounded-lg shrink-0" />}
          <div>
            <div className="font-display text-3xl leading-none text-foreground">FAAM WebServices</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-accent mt-1" data-testid="sidebar-structure-name">{structureName}</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.filter((n) => (!n.admin || isAdmin) && canSee(user, n.section)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border space-y-1">
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary transition-colors text-left text-sm text-muted-foreground hover:text-foreground"
          >
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {dark ? "Tema chiaro" : "Tema scuro"}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary transition-colors text-left"
              >
                <div className="w-9 h-9 rounded-full bg-accent text-accent-foreground grid place-items-center text-sm font-semibold font-heading">
                  {initials}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {isAdmin ? "Direttore" : "Receptionist"}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem data-testid="logout-button" onClick={doLogout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-card/90 backdrop-blur border-b border-border flex items-center justify-between px-4 h-14">
        <span className="font-display text-2xl">FAAM WebServices</span>
        <button data-testid="mobile-logout" onClick={doLogout} className="text-muted-foreground">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      <main className="flex-1 md:ml-64 pt-14 md:pt-0 min-h-screen">
        <div className="md:hidden flex overflow-x-auto gap-1 px-3 py-2 border-b border-border bg-card sticky top-14 z-30">
          {NAV.filter((n) => (!n.admin || isAdmin) && canSee(user, n.section)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium ${
                  isActive ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className={`p-5 sm:p-8 mx-auto ${location.pathname.startsWith("/mensa") ? "max-w-[1800px]" : "max-w-7xl"}`}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
